import { trace } from "@opentelemetry/api";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { DocumentLoadInstrumentation } from "@opentelemetry/instrumentation-document-load";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { resourceFromAttributes } from "@opentelemetry/resources";
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
  type Span,
  type SpanProcessor,
} from "@opentelemetry/sdk-trace-base";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";

/**
 * Browser OpenTelemetry init (ADR-0035): OTLP/HTTP -> OTel Collector hub -> PostHog.
 *
 * With an endpoint: real exporter + fetch/document-load auto-instrumentation, sampled at
 * [OtelConfig.sampleRatio] (keep aligned with the backend's OTEL_SAMPLING so a trace isn't
 * dropped on one hop and kept on the other), `traceparent` propagated ONLY to
 * [OtelConfig.propagateTraceUrls] (the API origin — the FE->BE->DB story).
 *
 * Without an endpoint (local dev, no collector): registers a bare provider so `getTracer()`
 * keeps working, exports nothing, patches nothing.
 */
export interface OtelConfig {
  /** OTLP/HTTP traces endpoint (VITE_OTEL_EXPORTER_OTLP_ENDPOINT). Falsy = no-op provider. */
  endpoint?: string;
  /** Head sampling ratio; default 0.1 — mirror the backend's OTEL_SAMPLING. */
  sampleRatio?: number;
  /** deployment.environment resource attribute (e.g. import.meta.env.MODE). */
  environment: string;
  /** Origins that receive the traceparent header (the API base URL). */
  propagateTraceUrls?: (string | RegExp)[];
}

/**
 * Resolve the OTLP/HTTP **traces** URL from the configured endpoint.
 *
 * `OTEL_EXPORTER_OTLP_ENDPOINT` is a BASE url in the OTel spec — the signal path is appended to it —
 * whereas the exporter's `url` option is the COMPLETE address and is used verbatim. Our variable is
 * named for the former and was being passed as the latter, so every span in every environment was
 * POSTed to the collector root and answered 404. Confirmed against staging: `POST /` -> 404,
 * `POST /v1/traces` -> 200. Traces were being dropped silently, because a span exporter has nobody
 * to report to.
 *
 * A value that already carries a path is left alone, so an explicit
 * `OTEL_EXPORTER_OTLP_TRACES_ENDPOINT`-style full URL still works and is never doubled up.
 */
export function tracesUrl(endpoint: string): string {
  try {
    const url = new URL(endpoint);
    if (url.pathname !== "/" && url.pathname !== "") return endpoint;
    url.pathname = "/v1/traces";
    return url.toString();
  } catch {
    // Not parseable as a URL: hand it back untouched rather than mangling it — the exporter's own
    // error is more useful than one invented here.
    return endpoint;
  }
}

let provider: WebTracerProvider | undefined;

// The same person key events/logs use (structured-logging convention): stamped onto every
// span so a trace can be pivoted to from a PostHog person profile. Fed by the PostHog
// adapter's identify().
let currentDistinctId: string | undefined;

export function setSpanDistinctId(distinctId: string): void {
  currentDistinctId = distinctId;
}

const distinctIdProcessor: SpanProcessor = {
  onStart(span: Span): void {
    if (currentDistinctId) span.setAttribute("posthogDistinctId", currentDistinctId);
  },
  onEnd(): void {},
  forceFlush: () => Promise.resolve(),
  shutdown: () => Promise.resolve(),
};

export function initOtel(config: OtelConfig): void {
  if (provider) return;

  if (!config.endpoint) {
    provider = new WebTracerProvider();
    provider.register();
    return;
  }

  provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      [ATTR_SERVICE_NAME]: "frontend-app",
      "deployment.environment": config.environment,
    }),
    sampler: new ParentBasedSampler({
      root: new TraceIdRatioBasedSampler(config.sampleRatio ?? 0.1),
    }),
    spanProcessors: [
      distinctIdProcessor,
      new BatchSpanProcessor(new OTLPTraceExporter({ url: tracesUrl(config.endpoint) })),
    ],
  });
  provider.register({ contextManager: new ZoneContextManager() });

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        propagateTraceHeaderCorsUrls: config.propagateTraceUrls ?? [],
      }),
      new DocumentLoadInstrumentation(),
    ],
  });
}

export function getTracer(name = "frontend") {
  return trace.getTracer(name);
}
